import FileDownloaderButton from 'components/FileDownloaderButton';
import IconButton from 'components/IconButton';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React from 'react';
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import PCAssigneeModal from './PCAssigneeModal';
import PCWatcherModal from './PCWatcherModal';
import {
  findingGridDataRefreshAtom,
  findingsDataQueryParamsAtom,
  selectedRowIdsAtom,
} from './state';

const BulkActions: React.FC = () => {
  const companyId = useAtomValue(companyIdAtom);
  const findingsDataQueryParams = useAtomValue(findingsDataQueryParamsAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);
  const count = selectedRowIds.length;
  const [isAssigneeModalOpen, setAssigneeModalOpen] = React.useState(false);
  const [isWatcherBulkModalOpen, setWatcherBulkModalOpen] =
    React.useState(false);
  const findingGridDataRefresh = useSetAtom(findingGridDataRefreshAtom);
  const [
    isMarkAllAsReadConfirmationModalOpen,
    setIsMarkAllAsReadConfirmationModalOpen,
  ] = React.useState(false);

  const appendParamsToUrl = (url, params) => {
    const urlObj = new URL(url, window.location.href);
    const existingParams = new URLSearchParams(urlObj.search);
    const newParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (!existingParams.has(key)) {
        newParams.set(key, value as string);
      }
    }

    // Add custom param finding_ids
    if (selectedRowIds.length > 0) {
      newParams.set('finding_ids', selectedRowIds.join(','));
    }

    const queryString = newParams.toString();
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${queryString ? queryString : ''}`;
  };

  const markAllAsRead = () => {
    callApi(
      `/pcm/posture-notes/mark-all-as-read/`,
      'POST',
      {},
      {
        posture_ids: selectedRowIds,
        company_id: companyId,
      },
    )
      .then(() => {
        // Refresh the grid data to update unread counts
        findingGridDataRefresh(Math.random().toString());
      })
      .catch((error) => {
        console.error('Error marking notes as read:', error);
        showFailureToast('Error marking notes as read.');
      })
      .finally(() => setIsMarkAllAsReadConfirmationModalOpen(false));
  };

  const MarkAllAsReadConfirmationModal = () => {
    return (
      <Modal
        isOpen={isMarkAllAsReadConfirmationModalOpen}
        toggle={() => setIsMarkAllAsReadConfirmationModalOpen(false)}
      >
        <ModalHeader>Mark all notes as read</ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to mark all notes as read for the selected (
            {count} ) findings?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setIsMarkAllAsReadConfirmationModalOpen(false)}
          >
            Cancel
          </Button>
          <Button color="primary" onClick={markAllAsRead}>
            Mark as read
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  return (
    <div className="col-auto text-right align-baseline">
      {count > 0 && (
        <>
          <span className="mr-1" style={{ fontSize: '1.2em' }}>
            <Badge color="primary" pill className="mx-1">
              {count}
            </Badge>
            Selected
          </span>
          <IconButton
            id="clearSelectionButton"
            iconClass="fas fa-times"
            tooltip="Clear all selections"
            onClick={() => setSelectedRowIds([])}
          />
          <span
            className="mx-2"
            style={{
              fontSize: '24px',
              color: 'gray',
              verticalAlign: 'middle',
            }}
          >
            &#124;
          </span>
        </>
      )}
      {/* <IconButton
        id="assignToUserButton"
        iconClass="fa fa-user-plus"
        tooltip="Bulk assign/unassign selected findings."
        onClick={function (): void {
          setAssigneeModalOpen(true);
        }}
        disabled={count === 0}
      />
      <IconButton
        id="assignWatchersButton"
        iconClass="fa fa-users"
        tooltip="Bulk add/remove watchers to selected findings."
        onClick={function (): void {
          setWatcherBulkModalOpen(true);
        }}
        disabled={count === 0}
      /> */}
      <IconButton
        id="markNotesAsReadButton"
        tooltip="Mark all notes as read for selected findings."
        onClick={() => setIsMarkAllAsReadConfirmationModalOpen(true)}
        disabled={count === 0}
        iconClass="tim-icons icon-chat-33"
      />
      <IconButton
        id="assignToUserButton"
        iconClass="fa fa-user-plus"
        tooltip="Bulk assign/unassign selected findings."
        onClick={function (): void {
          setAssigneeModalOpen(true);
        }}
        disabled={count === 0}
      />
      <IconButton
        id="assignWatchersButton"
        iconClass="fa fa-users"
        tooltip="Bulk add/remove watchers to selected findings."
        onClick={function (): void {
          setWatcherBulkModalOpen(true);
        }}
        disabled={count === 0}
      />
      <FileDownloaderButton
        buttonId="findings_export_all"
        iconClass="fa fa-file-export"
        tooltip="Export findings. All the active filters and row selection will be applied."
        fileName={`${localStorage
          .getItem('currentCompany')
          ?.toLocaleLowerCase()}_findings.csv`}
        method="GET"
        downloadUrl={
          companyId
            ? appendParamsToUrl(
                'pcm/posture/export-csv/',
                findingsDataQueryParams,
              )
            : ''
        }
        className="float-right"
        urlParams={{}}
        onSuccessCallback={() =>
          showSuccessToast('Success! File download completed.')
        }
        onFailureCallback={(message) => showFailureToast('Error! ' + message)}
      />
      {isAssigneeModalOpen && (
        <PCAssigneeModal
          isOpen={isAssigneeModalOpen}
          toggle={() => setAssigneeModalOpen(false)}
          isBulk={true}
        />
      )}
      {isWatcherBulkModalOpen && (
        <PCWatcherModal
          onClose={() => setWatcherBulkModalOpen(false)}
          postureIds={selectedRowIds}
          isBulk={true}
        />
      )}
      {isMarkAllAsReadConfirmationModalOpen && (
        <MarkAllAsReadConfirmationModal />
      )}
    </div>
  );
};

export default BulkActions;
